import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { join } from 'path';
import { Prompt, withRouter } from 'react-router-dom';
import { createHashHistory } from 'history';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { kebabCase } from 'lodash';
import { Card, CardBody, CardHeader, Col, Container, Nav, NavItem, NavLink, Row } from 'reactstrap';
import notificationConstants from '../../constants/notificationConstants';
import entityComposition from '../common/classes/entityComposition';
import FormModal from './formModal';
import SectionPanel from '../common/containers/sectionPanel';
import routeHelper from '../common/classes/routeHelper';
import EditorComponent from '../editor/editorComponent';
import Loader from '../common/loader';
import isDirectFormPath from './directFormHelpers';
const hashHistory = createHashHistory();

/**
 *  Given a section object, return the key for that section.
 */
const getSectionKey = section => {
  if (section && section.label) {
    return kebabCase(section.label);
  }
  return null;
};
const FormEditor = _ref => {
  var _active$format, _active$format2, _active$formType;
  let _ref$form = _ref.form,
    active = _ref$form.active,
    loading = _ref$form.loading,
    pendingChanges = _ref$form.pendingChanges,
    _ref$formActions = _ref.formActions,
    change = _ref$formActions.change,
    serverShow = _ref$formActions.serverShow,
    serverSave = _ref$formActions.serverSave,
    showCancel = _ref$formActions.showCancel,
    operationComplete = _ref$formActions.operationComplete,
    operationPending = _ref$formActions.operationPending,
    _ref$formTitle = _ref.formTitle,
    formTitle = _ref$formTitle === void 0 ? '' : _ref$formTitle,
    id = _ref.id,
    location = _ref.location,
    _ref$match = _ref.match,
    section = _ref$match.params.section,
    url = _ref$match.url,
    validationError = _ref.notificationActions.validationError,
    _ref$related = _ref.related,
    _ref$related2 = _ref$related === void 0 ? {
      loading: false,
      pendingChanges: false,
      onBack: null,
      onSave: null,
      onValidate: null,
      suppressUnsavedChangesNotification: false
    } : _ref$related,
    _ref$related2$loading = _ref$related2.loading,
    relatedLoading = _ref$related2$loading === void 0 ? false : _ref$related2$loading,
    _ref$related2$pending = _ref$related2.pendingChanges,
    relatedPendingChanges = _ref$related2$pending === void 0 ? false : _ref$related2$pending,
    _ref$related2$onBack = _ref$related2.onBack,
    relatedBack = _ref$related2$onBack === void 0 ? null : _ref$related2$onBack,
    relatedCancel = _ref$related2.onCancel,
    _ref$related2$onSave = _ref$related2.onSave,
    relatedSave = _ref$related2$onSave === void 0 ? null : _ref$related2$onSave,
    _ref$related2$onValid = _ref$related2.onValidate,
    relatedValidate = _ref$related2$onValid === void 0 ? null : _ref$related2$onValid,
    _ref$related2$suppres = _ref$related2.suppressUnsavedChangesNotification,
    suppressUnsavedChangesNotification = _ref$related2$suppres === void 0 ? false : _ref$related2$suppres,
    _ref$hideBackButton = _ref.hideBackButton,
    hideBackButton = _ref$hideBackButton === void 0 ? true : _ref$hideBackButton,
    _ref$sectionsLabel = _ref.sectionsLabel,
    sectionsLabel = _ref$sectionsLabel === void 0 ? 'Sections' : _ref$sectionsLabel,
    _ref$showSave = _ref.showSave,
    showSave = _ref$showSave === void 0 ? true : _ref$showSave,
    _ref$favoritesEnabled = _ref.favoritesEnabled,
    favoritesEnabled = _ref$favoritesEnabled === void 0 ? false : _ref$favoritesEnabled;
  const showFavoriteButton = favoritesEnabled && !isDirectFormPath(location);

  // define an instance variable so that useCallback methods have the latest data
  const activeRef = useRef();
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  // control the Loader, only displaying it during initial load and not saves
  const _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    loaded = _useState2[0],
    setLoaded = _useState2[1];
  useEffect(() => {
    if (!loaded && !loading && !relatedLoading) {
      setLoaded(true);
    }
  }, [loaded, loading, relatedLoading]);
  const sections = useMemo(() => {
    if (active.format && active.format.children) {
      // Return only visible sections (i.e. visible is not set to false)
      return active.format.children.map(child => ({
        label: child.label,
        format: child
      })).filter(currentSection => currentSection.format.visible !== undefined && currentSection.format.visible === true || currentSection.format.visible === undefined);
    }
    return [];
  }, [active.format]);

  // Track previous section/sections to prevent unnecessary useEffect runs
  // Initialize to undefined so first run always executes
  // Must be declared AFTER sections to avoid temporal dead zone error
  const prevSectionRef = useRef(undefined);
  const prevSectionKeysRef = useRef(undefined);
  const _useState3 = useState(() => {
      if (section) return section;

      // return the first section.
      if (sections && sections.length > 0) {
        return getSectionKey(sections[0]);
      }
      return 'general';
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    sectionIndex = _useState4[0],
    setSectionIndex = _useState4[1];
  const sectionFormat = useMemo(() => {
    const currentSection = sections.find(cs => getSectionKey(cs) === sectionIndex);
    return currentSection ? currentSection.format : undefined;
  }, [sectionIndex, sections]);
  const handleCancel = useCallback(() => {
    if (relatedCancel) relatedCancel();
    showCancel();
  }, [relatedCancel, showCancel]);
  const handleBack = useCallback(() => {
    if (relatedBack) return relatedBack();
    return hashHistory.goBack();
  }, [relatedBack]);
  const handleViewPDF = () => {
    if (active.isNew) {
      return;
    }
    const pathnameParts = location.pathname.split('/');
    const pdfviewRoute = "/".concat(pathnameParts[1], "/").concat(pathnameParts[2], "/").concat(pathnameParts[3], "/").concat(pathnameParts[4], "/pdfview");
    routeHelper.updateURL(pdfviewRoute);
  };

  // create a change action with the new info. need to prepend the content object to this keypath
  const handleChange = useCallback(async _ref2 => {
    let path = _ref2.path,
      value = _ref2.value,
      changeModifiers = _ref2.changeModifiers;
    return change(id, join('content', path), value, changeModifiers);
  }, [change, id]);

  // load db record
  useEffect(() => {
    const current = activeRef.current;
    if (id && id !== current.id) {
      serverShow(id);
      setLoaded(false); // display Loader
    }
  }, [activeRef, id, serverShow]);
  const handleSave = useCallback(async () => {
    let errors = [];
    if (relatedValidate) {
      errors = relatedValidate();
    }
    if (relatedPendingChanges && relatedSave) {
      // call any additional save operations passed into this component. In the case of jobs,
      // the validation method will run again and errors will be displayed.
      const relatedSaveResult = await relatedSave();
      if (!relatedSaveResult) {
        return; // don't save the form if relatedSave fails
      }
    }

    // don't save Form info if there are validation errors in the related data
    if (pendingChanges && !errors.length) {
      const version = active.content && active.content.version || undefined;
      await serverSave(version);
    }
    if (errors.length > 0) {
      validationError({
        list: errors,
        id: notificationConstants.VALIDATION_ERROR
      }, {
        autoClose: 10000
      });
    }
  }, [relatedValidate, relatedPendingChanges, relatedSave, pendingChanges, active.content, serverSave, validationError]);
  const handleSelectSection = sectionKey => {
    setSectionIndex(sectionKey);
    let newLocation = location.pathname;
    if (!section) {
      newLocation = "".concat(newLocation, "/").concat(sectionKey);
    } else if (sectionKey !== section) {
      newLocation = newLocation.replace(section, sectionKey);
    }
    if (location.pathname !== newLocation) {
      hashHistory.replace(newLocation);
    }
  };

  /**
   * Handle the transition away from the page using a custom component object, which
   * is expected by transitionAsync. The entity built below allows control over two
   * entities: the form and the optional related entity. The object itself is weird and probably
   * too legacy-specific.
   */
  const handleTransition = useCallback(calledLocation => {
    const transitionProps = {
      handleSave,
      props: {
        entity: 'form',
        canEdit: true,
        formandrelated: {
          loading: loading || relatedLoading,
          pendingChanges: pendingChanges || relatedPendingChanges
        },
        formandrelatedActions: {
          // clear the form and related entity when showCancel is called in routeHelper
          showCancel: handleCancel
        },
        match: {
          url
        }
      }
    };
    const sectionRoutes = sections.map(s => {
      if (section) {
        return location.pathname.replace(section, getSectionKey(s));
      }
      return "".concat(location.pathname, "/").concat(getSectionKey(s));
    });
    return routeHelper.transitionAsync(transitionProps, 'formandrelated', calledLocation, sectionRoutes);
  }, [handleSave, loading, location.pathname, pendingChanges, section, sections, handleCancel, url, relatedLoading, relatedPendingChanges]);

  // Sync section from URL with sectionIndex state and validate sectionIndex
  // Combined into one useEffect to avoid conflicts between multiple effects
  useEffect(() => {
    if (!sections.length) {
      return;
    }

    // Compare actual section keys to detect real changes (not just array reference changes)
    const sectionKeys = sections.map(s => getSectionKey(s));
    const sectionKeysStr = sectionKeys.join(',');
    const prevSectionKeysStr = prevSectionKeysRef.current || '';

    // Skip if section and section keys haven't changed (prevents unnecessary runs)
    if (section === prevSectionRef.current && sectionKeysStr === prevSectionKeysStr) {
      return;
    }

    // Update refs
    prevSectionRef.current = section;
    prevSectionKeysRef.current = sectionKeysStr;
    setSectionIndex(currentIndex => {
      // If section from URL is provided and valid, use it
      if (section && sectionKeys.includes(section)) {
        // Only update if different to prevent unnecessary re-renders
        if (section !== currentIndex) {
          return section;
        }
        return currentIndex;
      }

      // If current section is valid, keep it (no change needed)
      if (sectionKeys.includes(currentIndex)) {
        return currentIndex;
      }

      // Current section is invalid and no valid URL section, use first section
      // Only update if different to prevent unnecessary re-renders
      const firstSection = sectionKeys[0];
      if (firstSection !== currentIndex) {
        return firstSection;
      }
      return currentIndex;
    });
    // Both dependencies needed: sections for validation, section for URL sync
  }, [sections, section]);
  let sectionKeyPath = null;
  let hasSection = false;
  if (sectionFormat && sectionFormat.children) {
    hasSection = true;
    sectionKeyPath = sectionFormat.contentPath ? sectionFormat.contentPath : '/';
  }
  const displayTitle = !formTitle && active.content ? active.content.name : formTitle;
  const isFormLoaded = active && active.id;
  return /*#__PURE__*/React.createElement(Loader, {
    top: "50%",
    loaded: loaded
  }, /*#__PURE__*/React.createElement(Container, {
    "data-testid": "formEditor"
  }, /*#__PURE__*/React.createElement(Prompt, {
    when: (pendingChanges || relatedPendingChanges) && !suppressUnsavedChangesNotification,
    message: handleTransition
  }), /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    sm: 3,
    className: active !== null && active !== void 0 && (_active$format = active.format) !== null && _active$format !== void 0 && _active$format.fullWidth ? 'd-none' : ''
  }, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(CardHeader, {
    className: "card-heading"
  }, /*#__PURE__*/React.createElement("h3", {
    className: "t-fe-sidebar-title"
  }, sectionsLabel)), /*#__PURE__*/React.createElement(CardBody, null, /*#__PURE__*/React.createElement(Nav, {
    vertical: true,
    pills: true
  }, sections.map(displaySection => /*#__PURE__*/React.createElement(NavItem, {
    style: {
      maxWidth: '100%'
    },
    className: "t-fe-nav-section-".concat(getSectionKey(displaySection)),
    active: sectionIndex === getSectionKey(displaySection),
    key: "NAV_".concat(displaySection.label)
  }, /*#__PURE__*/React.createElement(NavLink, {
    onClick: () => handleSelectSection(getSectionKey(displaySection))
  }, displaySection.label))))))), /*#__PURE__*/React.createElement(Col, {
    sm: active !== null && active !== void 0 && (_active$format2 = active.format) !== null && _active$format2 !== void 0 && _active$format2.fullWidth ? 12 : 9
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(SectionPanel, {
    isPending: pendingChanges || relatedPendingChanges,
    isSaveDisabled: loading || relatedLoading,
    onSave: handleSave,
    name: displayTitle,
    onBack: hideBackButton ? null : handleBack,
    onNavButtonClick: handleViewPDF,
    navButtonTitle: isFormLoaded ? 'View PDF' : 'Loading...',
    isNavButtonVisible: !active.isNew && !!((_active$formType = active.formType) !== null && _active$formType !== void 0 && _active$formType.formatOutput),
    showSave: showSave,
    showFavoriteButton: showFavoriteButton
  }, hasSection ? /*#__PURE__*/React.createElement(EditorComponent, {
    reduxEntity: "form",
    path: sectionKeyPath,
    format: sectionFormat,
    onChange: handleChange,
    operationComplete: operationComplete,
    operationPending: operationPending
  }) : /*#__PURE__*/React.createElement("div", {
    style: {
      minHeight: '300px'
    }
  }, active.content && loaded && /*#__PURE__*/React.createElement("p", null, "This section", /*#__PURE__*/React.createElement("strong", null, "\xA0", sectionFormat && sectionFormat.label ? sectionFormat.label : '', "\xA0"), "is not defined in the editor."))))))), /*#__PURE__*/React.createElement(FormModal, null));
};
FormEditor.propTypes = {
  form: PropTypes.shape({
    active: PropTypes.shape({
      content: PropTypes.shape({
        formTypeID: PropTypes.string,
        name: PropTypes.string,
        version: PropTypes.string
      }),
      format: PropTypes.shape({
        fullWidth: PropTypes.bool,
        children: PropTypes.arrayOf(PropTypes.shape({
          label: PropTypes.string
        }))
      }),
      formType: PropTypes.shape({
        formatOutput: PropTypes.string
      }),
      id: PropTypes.string,
      isNew: PropTypes.bool,
      name: PropTypes.string
    }),
    loading: PropTypes.bool,
    pendingChanges: PropTypes.bool
  }).isRequired,
  formActions: PropTypes.shape({
    change: PropTypes.func,
    operationPending: PropTypes.func,
    operationComplete: PropTypes.func,
    changeQuietly: PropTypes.func,
    serverSave: PropTypes.func,
    serverShow: PropTypes.func,
    showCancel: PropTypes.func
  }).isRequired,
  formTitle: PropTypes.string,
  id: PropTypes.string.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      section: PropTypes.string,
      id: PropTypes.string
    }),
    url: PropTypes.string
  }).isRequired,
  notificationActions: PropTypes.shape({
    validationError: PropTypes.func
  }).isRequired,
  related: PropTypes.shape({
    loading: PropTypes.bool,
    pendingChanges: PropTypes.bool,
    onBack: PropTypes.func,
    onCancel: PropTypes.func,
    onSave: PropTypes.func,
    onValidate: PropTypes.func,
    onShow: PropTypes.func,
    suppressUnsavedChangesNotification: PropTypes.bool
  }),
  hideBackButton: PropTypes.bool,
  sectionsLabel: PropTypes.string,
  showSave: PropTypes.bool,
  favoritesEnabled: PropTypes.bool
};
export { FormEditor as TestFormEditor };
export default withRouter(entityComposition(FormEditor, ['form', 'settings'], {
  favoritesEnabled: state => {
    var _state$settings;
    return ((_state$settings = state.settings) === null || _state$settings === void 0 || (_state$settings = _state$settings.settings) === null || _state$settings === void 0 ? void 0 : _state$settings.favoritesEnabled) || false;
  }
}, ['notification']));